import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { StepProps } from '../MultiStepModal';
import { certificatesApi, getApiErrorMessage } from '../../../utils/api';

export default function CertificateTemplateUploadStep({ data, onDataChange }: StepProps) {
  const [fileName, setFileName] = useState<string>('');
  const [phase, setPhase] = useState<'idle' | 'extract' | 'infer'>('idle');

  const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    let handle: any;
    const timeoutPromise = new Promise<never>((_, reject) => {
      handle = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (handle) clearTimeout(handle);
    }
  };

  const normalizePdfFieldLabel = (rawName: string) => {
    const raw = String(rawName || '').trim();
    if (!raw) return '';

    let s = raw;
    s = s.replace(/%[0-9A-Fa-f]{2}/g, ' ');

    s = s.replace(/[_.\-/]+/g, ' ');

    s = s.replace(/([A-Za-z])(\d)/g, '$1 $2');
    s = s.replace(/(\d)([A-Za-z])/g, '$1 $2');

    s = s.replace(/([a-z])([A-Z])/g, '$1 $2');
    s = s.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
    s = s.replace(/^\s*\d{3,}(?=[A-Za-z])/g, '');

    const parts = s
      .split(/\s+/)
      .map((p) => p.trim())
      .filter(Boolean);

    const looksLikeIdToken = (p: string) => {
      if (!p) return true;
      if (/^[0-9]{3,}$/.test(p)) return true;
      if (/^[0-9A-Fa-f]{8,}$/.test(p)) return true;
      if (/^[0-9A-Za-z]{10,}$/.test(p) && /\d/.test(p)) return true;
      return false;
    };

    const cleanedParts: string[] = [];
    for (const p of parts) {
      const m = p.match(/^(\d+)([A-Za-z].*)$/);
      if (m) {
        const digits = m[1];
        const rest = m[2];
        if (digits.length <= 2) {
          cleanedParts.push(digits, rest);
          continue;
        }
        if (!cleanedParts.length) continue;
        cleanedParts.push(rest);
        continue;
      }

      if (!cleanedParts.length && looksLikeIdToken(p)) continue;
      cleanedParts.push(p);
    }

    let out = cleanedParts.join(' ').replace(/\s+/g, ' ').trim();
    if (!out) out = raw;

    if (out === out.toLowerCase()) {
      out = out
        .split(' ')
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(' ');
    }

    return out.slice(0, 200);
  };

  const toFiniteNumberOrUndef = (v: any) => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const parseContextLabel = (label: string) => {
    const s = String(label || '').replace(/\s+/g, ' ').trim();
    const m = s.match(/^([A-Za-z][A-Za-z ]{1,30})\s*(\d{1,2})\b\s*(.*)$/);
    if (!m) return null;
    return {
      base: String(m[1] || '').replace(/\s+/g, ' ').trim(),
      n: Number(m[2]),
      rest: String(m[3] || '').replace(/\s+/g, ' ').trim(),
    };
  };

  const sortExtractedFields = (arr: any[], strategy: 'row-major' | 'column-major') => {
    const withIdx = arr.map((f, idx) => ({ f, idx }));

    const numOrNull = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const pageOrZero = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

    withIdx.sort((a, b) => {
      const ap = pageOrZero(a.f.pageIndex);
      const bp = pageOrZero(b.f.pageIndex);
      if (ap !== bp) return ap - bp;

      const ax = numOrNull(a.f.x);
      const bx = numOrNull(b.f.x);
      const ay = numOrNull(a.f.y);
      const by = numOrNull(b.f.y);

      const hasGeomA = ax !== null && ay !== null;
      const hasGeomB = bx !== null && by !== null;
      if (hasGeomA && !hasGeomB) return -1;
      if (!hasGeomA && hasGeomB) return 1;

      if (hasGeomA && hasGeomB) {
        if (strategy === 'column-major') {
          if (ax !== bx) return (ax as number) - (bx as number);
          if (ay !== by) return (by as number) - (ay as number);
        } else {
          if (ay !== by) return (by as number) - (ay as number);
          if (ax !== bx) return (ax as number) - (bx as number);
        }
      }

      const ac = parseContextLabel(String(a.f.displayLabel || ''));
      const bc = parseContextLabel(String(b.f.displayLabel || ''));
      if (ac && bc && ac.base && bc.base && ac.base === bc.base) {
        if (strategy === 'column-major') {
          if (ac.n !== bc.n) return ac.n - bc.n;
          if (ac.rest !== bc.rest) return ac.rest.localeCompare(bc.rest);
        } else {
          if (ac.rest !== bc.rest) return ac.rest.localeCompare(bc.rest);
          if (ac.n !== bc.n) return ac.n - bc.n;
        }
      }

      return a.idx - b.idx;
    });

    return withIdx.map((w) => w.f);
  };

  const inferOrdering = (arr: any[]): 'row-major' | 'column-major' => {
    const points = arr
      .map((f) => ({ x: f.x, y: f.y }))
      .filter((p) => typeof p.x === 'number' && Number.isFinite(p.x) && typeof p.y === 'number' && Number.isFinite(p.y));

    if (points.length < 8) return 'row-major';

    const round = (n: number) => Math.round(n / 5) * 5;
    const xs = Array.from(new Set(points.map((p) => round(p.x)).sort((a, b) => a - b)));
    const ys = Array.from(new Set(points.map((p) => round(p.y)).sort((a, b) => a - b)));

    if (xs.length >= 3 && ys.length >= 3) return 'column-major';

    return 'row-major';
  };

  const canExtract = useMemo(() => {
    return Boolean(
      String(data.certificatePrefix || '').trim() &&
        String(data.certificateTypeName || '').trim() &&
        String(data.templateLabel || '').trim() &&
        data.templateFile instanceof File,
    );
  }, [data.certificatePrefix, data.certificateTypeName, data.templateLabel, data.templateFile]);

  const extract = useMutation({
    mutationFn: async () => {
      const file = data.templateFile as File;
      setPhase('extract');
      const res = await withTimeout(
        certificatesApi.extractTemplateFields({
          file,
          certificatePrefix: String(data.certificatePrefix || ''),
          certificateTypeName: String(data.certificateTypeName || ''),
          templateLabel: String(data.templateLabel || ''),
        }),
        130000,
        'Extract fields',
      );

      const extracted = res.data?.data || res.data;
      const rawFields: any[] = Array.isArray(extracted?.fields) ? extracted.fields : [];

      const existingOrdering =
        (data as any)?.fieldOrdering === 'column-major'
          ? 'column-major'
          : (data as any)?.fieldOrdering === 'row-major'
            ? 'row-major'
            : null;

      const mapped = rawFields.map((f: any, idx: number) => ({
        pdfFieldName: f.pdf_field_name,
        displayLabel: (() => {
          const pdfName = String(f.pdf_field_name || '');
          const normalized = normalizePdfFieldLabel(pdfName);
          if (normalized) return normalized;
          const suggested = String(f.display_label_suggested || '').trim();
          if (suggested) return suggested;
          return `Field ${idx + 1}`;
        })(),
        fieldType: f.field_type || 'text',
        required: false,
        hint: '',
        pageIndex: toFiniteNumberOrUndef(f.page_index),
        x: toFiniteNumberOrUndef(f.x),
        y: toFiniteNumberOrUndef(f.y),
        width: toFiniteNumberOrUndef(f.width),
        height: toFiniteNumberOrUndef(f.height),
      }));

      const ordering = existingOrdering ?? inferOrdering(mapped);
      const sorted = sortExtractedFields(mapped, ordering);
      const fields = sorted.map((f: any, idx: number) => ({ ...f, orderIndex: idx }));

      setPhase('infer');
      const inferRes = await withTimeout(
        certificatesApi.inferTemplateSchema({
          certificatePrefix: String(data.certificatePrefix || '').trim(),
          certificateTypeName: String(data.certificateTypeName || '').trim(),
          templateLabel: String(data.templateLabel || '').trim(),
          pdfStoragePath: String(extracted?.pdfStoragePath || '').trim(),
          pages: Array.isArray(extracted?.pages) ? extracted.pages : [],
          documentIntel: (extracted as any)?.documentIntel,
        }),
        130000,
        'Build schema',
      );

      const inferPayload = inferRes.data;
      const inferData = inferPayload?.data || inferPayload;

      return {
        extracted,
        ordering,
        fields,
        infer: inferData,
      };
    },
    onSuccess: (result: any) => {
      setPhase('idle');
      const extracted = result?.extracted;
      const ordering = result?.ordering;
      const fields = result?.fields;
      const infer = result?.infer;

      onDataChange({
        pdfStoragePath: extracted?.pdfStoragePath,
        templatePreviewUrl: extracted?.previewUrl,
        templatePages: extracted?.pages,
        fieldOrdering: ordering,
        fields,
        importId: infer?.importId,
        inferredSchema: infer?.schema,
        documentIntel: (infer as any)?.documentIntel || (extracted as any)?.documentIntel,
        lowConfidenceFields: infer?.lowConfidenceFields,
        lowConfidenceCount: infer?.lowConfidenceCount,
      });
    },
    onError: () => {
      setPhase('idle');
    },
  });

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Upload PDF</h3>
        <p className="step-form-subtitle">
          Upload any certificate PDF. We reconstruct layout (text, tables, checklists, form widgets) with PyMuPDF and
          build multi-step forms from that structure — not from raw field names alone.
        </p>
      </div>

      <div className="step-form-content">
        <div className="form-group">
          <label className="form-label">Template PDF *</label>
          <input
            type="file"
            accept="application/pdf"
            className="form-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setFileName(file.name);
              onDataChange({ templateFile: file });
            }}
          />
          {fileName ? <div className="text-xs text-[#686E6C] mt-1">Selected: {fileName}</div> : null}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => extract.mutate()}
            disabled={!canExtract || extract.isPending}
          >
            {extract.isPending ? (phase === 'infer' ? 'Building form…' : 'Parsing PDF…') : 'Parse PDF & build form'}
          </button>

          {data.templatePreviewUrl ? (
            <a
              href={data.templatePreviewUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost"
            >
              Preview
            </a>
          ) : null}
        </div>

        {extract.isError ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {getApiErrorMessage(extract.error) || 'Failed to extract PDF fields'}
          </div>
        ) : null}

        {Array.isArray(data.fields) && data.fields.length > 0 ? (
          <div className="mt-4 rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] px-4 py-3 text-sm">
            Extracted {data.fields.length} fields.
          </div>
        ) : null}
      </div>
    </div>
  );
}
